<template>
  <div
    :class="{
      'osk-product-comment': true,
      [`osk-product-comment-${comment.id}`]: true,
    }"
  >
    <div class="osk-product-comment__content">
      <UikitUserAvatar
        v-if="comment.publisher"
        :user="comment.publisher"
        size="m"
        class="osk-product-comment__avatar"
      />

      <div class="osk-product-comment__right">
        <UikitLoaderInner
          v-if="deleteCommentLoadingId === comment.id"
          small
        />

        <nuxt-link
          v-if="comment.publisher?.id && comment.publisher.nickname"
          :to="`/profile/${comment.publisher.id}`"
          class="osk-product-comment__user"
        >
          {{ comment.publisher.nickname }}
        </nuxt-link>

        <div class="osk-product-comment__text">
          {{ commentText }}
        </div>

        <div
          v-if="comment.images && comment.images.length"
          class="osk-product-comment__images"
        >
          <div
            v-for="(image, index) in comment.images"
            :key="image"
            class="osk-product-comment__image-wrapper"
            @click="handleImageClick(index)"
          >
            <img
              :src="getImgPath(image)"
              class="osk-product-comment__image"
              @error="onImgError"
            >
          </div>
        </div>

        <div class="osk-product-comment__links">
          <ClientOnly>
            <span class="osk-product-comment__link">
              {{ commentTime }}
            </span>
          </ClientOnly>

          <span
            v-if="isCurrentUser"
            class="osk-product-comment__link osk-product-comment__delete"
            @click="handleDeleteComment"
          >
            {{ $t('common.delete') }}
          </span>

          <div
            v-if="isInternational"
            class="osk-product-comment__link osk-product-comment__translate-button"
            @click="handleClickTranslate"
          >
            <span class="osk-translate-button__text">
              {{ $t('product.productComment.translateBtn.showTranslation') }}
            </span>
            <UikitLoader
              v-if="translationPending"
              mini
            />
          </div>

          <span
            v-if="isAuth"
            class="osk-product-comment__link osk-product-comment__answer"
            :class="{
              'osk-product-comment__answer-choosed': isChoosed
            }"
            @click="handleAnswer"
          >
            {{ $t('components.notifications.notificationsItem.answer') }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="comment.subComments?.length"
      class="osk-product-comment__list"
    >
      <ProductCommentsComment
        v-for="(item, index) in comment.subComments"
        :key="index"
        :comment="item"
        :reply-comment="replyComment"
        :refresh="refresh"
        @answer="emits('answer', item)"
        @image-click="emits('imageClick', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useApi } from '~/restAPI';
import { Translate } from '~/restAPI/Translate';

import { CommentDTO } from '~/restAPI/data-contracts';
import { useProductService } from '~/services/product';

const props = defineProps<{
  comment: CommentDTO
  replyComment?: CommentDTO
  refresh: () => void
}>();

const emits = defineEmits<{
  (e: 'imageClick', data: {index: number, images: string[]}): void,
  (e: 'answer', comment: CommentDTO): void,
}>();

const { createApiInstance } = useApi();
const translateApi = createApiInstance(Translate);
const { deleteComment } = useProductService();
const { onImgError, getImgPath } = useImgCdnPath();
const { isAuth, account } = useAccountStore();
const { getTimeFromNow } = useDateFormatter();
const { isInternational } = useInternational();

const deleteCommentLoadingId = ref<number>();

const translation = ref<string | null>(null);
const isTranslationShown = ref(false);

const commentText = computed<string | undefined>(() => {
  if (translation.value && isTranslationShown.value) {
    return translation.value;
  }
  return props.comment.text;
});
const isCurrentUser = computed<boolean>(() => isAuth.value && props.comment.publisher!.id! === account.value.id);
const isChoosed = computed(() => props.replyComment?.id === props.comment.id);

const {
  data: translationData,
  execute: getTranslation,
  pending: translationPending,
} = useLazyAsyncData(() => translateApi.getCommentTranslate({ commentId: props.comment.id })
  .then((res) => res?.data.data), { immediate: false });

translationPending.value = false;

function handleImageClick(index: number) {
  emits('imageClick', {
    index,
    images: props.comment.images!,
  });
}

const commentTime = computed(() => {
  if (!props.comment.publishedAtTime) return '';

  const date = new Date(props.comment.publishedAtTime * 1000);

  return getTimeFromNow(date);
});

async function handleDeleteComment() {
  if (!props.comment.id) return;

  try {
    deleteCommentLoadingId.value = props.comment.id;

    const { data } = await useAsyncData(() => deleteComment(props.comment.id!));

    if (data.value) {
      await props.refresh();
    }
  } catch (error) {
    console.error(error);
  } finally {
    deleteCommentLoadingId.value = undefined;
  }
}

async function handleClickTranslate() {
  if (translation.value) {
    isTranslationShown.value = !isTranslationShown.value;
    return;
  }

  await getTranslation();

  translation.value = translationData.value?.text || '';

  if (translation.value.length) {
    isTranslationShown.value = true;
  }
}

function handleAnswer() {
  emits('answer', props.comment);
}

</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.osk-product-comment {
  &:not(:last-child) {
    margin-bottom: $indent-mmedium;
  }

  &__content {
    display: flex;
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__right {
    position: relative;
  }

  &__user {
    font-weight: $font-weight-bold;
    margin-bottom: $indent-xsmall;
    cursor: pointer;
    display: inline-block;

    &:hover {
      text-decoration: underline;
    }
  }

  &__text {
    margin-bottom: $indent-xsmall;
  }

  &__links {
    @include font-style($font-size-caption, $line-height-caption, $font-weight-semibold);
    color: $grey;
    display: flex;
    align-items: baseline;
  }

  &__link {
    &:not(:last-child) {
      margin-right: $indent-compact;
    }
  }

  &__answer {
    color: $blue;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__answer-choosed {
    text-decoration: underline;
    cursor: default;
    pointer-events: none;
  }

  &__delete {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__translate-button {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    margin-top: 12px;
    font-weight: $font-weight-semibold;
    color: $grey;

    .osk-translate-button__text:hover {
      text-decoration: underline;
    }
  }

  &__list {
    padding-left: 52px;
    margin-top: 20px;

    .osk-product-comment__avatar {
      width: 30px;
      height: 30px;
    }
  }

  &__images {
    display: flex;
    margin-bottom: 4px;
  }

  &__image-wrapper {
    width: 85px;
    height: 85px;
    margin-right: 4px;
    cursor: pointer;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 2px;
    overflow: hidden;
  }
}
</style>
